import { useEffect, useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  CoCreationForm,
  CoCreationFormElement,
  QuestionType,
} from '@factorialco/f0-react/dist/experimental'
import { FormDetailDocument } from 'generated/resources/graphql'
import { isEqual } from 'lodash'

import Loading from 'modules/core/components/Loading'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useOne } from 'modules/ai/lib/useOne'
import {
  FromFormToQuestionnaire,
  FromFragmentToQuestionnaire,
  FromQuestionaireToMutationVariables,
  FromQuestionnaireToForm,
} from 'modules/forms/components/newFormEditor/helpers'
import { useUpdateNewForm } from 'modules/forms/hooks/useForms'
import { useResourceHeader } from 'modules/layout/components/ResponsivePage/hooks'

import { FormEditorActions } from './formEditorActions'
import { AgentStateType, QuestionnaireType } from './types'

const defaultQuestionnaire: QuestionnaireType = {
  title: '',
  description: '',
  elements: [],
}

function isDefaultQuestionnaire(q: QuestionnaireType): boolean {
  return q.title === '' && q.description === '' && q.elements.length === 0
}

function hasLoadedData(q: QuestionnaireType): boolean {
  return q.title !== '' || q.description !== '' || q.elements.length > 0
}

function getSectionsCount(questionnaire: QuestionnaireType): number {
  return questionnaire.elements.filter((element) => element.type === 'section').length
}

function getQuestionsCount(questionnaire: QuestionnaireType): number {
  const rootQuestions = questionnaire.elements.filter(
    (element) => element.type !== 'section'
  ).length
  const sectionQuestions = questionnaire.elements
    .filter((element) => element.type === 'section')
    .flatMap((section) => section.elements.length)
  return rootQuestions + sectionQuestions.reduce((acc, curr) => acc + curr, 0)
}

interface Props {
  id?: string
  resourceType: string
  formType?: AgentStateType['formType']
  allowedQuestionTypes?: QuestionType[]
}

const NewFormEditor = ({ id, resourceType, formType, allowedQuestionTypes }: Props) => {
  const { updateHeader, updateMetadataProperty } = useResourceHeader()

  const { updateForm } = useUpdateNewForm()

  // Todo: Move this query to this component
  const { data: formData, loading: loadingForm } = useQuery(FormDetailDocument, {
    variables: { id: id ?? '', resourceType },
    skip: !id,
  })

  const formIsDraft = formData?.forms?.form?.status === 'draft'

  const questionnaire = useMemo(
    () =>
      formData?.forms?.form
        ? FromFragmentToQuestionnaire(formData.forms.form)
        : defaultQuestionnaire,
    [formData?.forms?.form]
  )

  const initialAgentState: AgentStateType = useMemo(
    () => ({
      formType,
      questionnaire,
      updateForm: true,
    }),
    [formType, questionnaire]
  )

  const {
    coCreationData: agentState,
    setCoCreationData: setAgentState,
    isThinking,
  } = useOne<AgentStateType>(['surveys'], initialAgentState)

  const [savedState, setSavedState] = useState<QuestionnaireType>(defaultQuestionnaire)

  // Sync API-loaded questionnaire into agent state once (when agent is still default).
  // Also set savedState so isDirty stays false after load.
  useEffect(() => {
    if (!id || !hasLoadedData(questionnaire) || !setAgentState) return
    const agentIsDefault = !agentState || isDefaultQuestionnaire(agentState.questionnaire)
    if (!agentIsDefault) return

    setAgentState({
      formType,
      questionnaire,
      updateForm: true,
    })
    setSavedState(questionnaire)
  }, [id, formType, questionnaire, agentState, setAgentState])

  // Update resource header from agent state (title, description, questions count).
  useEffect(() => {
    if (loadingForm || !agentState?.questionnaire) return
    updateHeader((header) => ({
      ...(header || {}),
      title: agentState.questionnaire.title,
      description: agentState.questionnaire.description,
    }))
    updateMetadataProperty(i18n.t('surveys.header.metadata.questions'), {
      type: 'text' as const,
      content: `${getQuestionsCount(agentState.questionnaire)}`,
    })
    updateMetadataProperty(i18n.t('surveys.header.metadata.sections'), {
      type: 'text' as const,
      content: `${getSectionsCount(agentState.questionnaire)}`,
    })
  }, [loadingForm, agentState?.questionnaire, updateHeader, updateMetadataProperty])

  const isDirty = useMemo(() => {
    if (!agentState?.questionnaire) return false
    return !isEqual(agentState.questionnaire, savedState)
  }, [agentState?.questionnaire, savedState])

  const onDiscard = () => {
    if (agentState && setAgentState && questionnaire) {
      setAgentState({ ...agentState, questionnaire, updateForm: true })
      setSavedState(questionnaire)
    }
  }

  const onSave = async () => {
    if (!formData?.forms.form || !agentState?.questionnaire || !agentState || !setAgentState) return

    // Todo: Move this mutation to this component
    const result = await updateForm({
      ...FromQuestionaireToMutationVariables(
        formData.forms.form.id,
        formData.forms.form.anonymous,
        agentState.questionnaire
      ),
    })
    if (result.success) {
      setSavedState(agentState.questionnaire)
      setAgentState({
        ...agentState,
        questionnaire: agentState.questionnaire,
        updateForm: true,
      })

      app.setFlash({
        type: 'correct',
        message: i18n.t('trainings.surveys.create.edit_modal.form_success_message'),
        time: 1000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('trainings.surveys.create.edit_modal.form_error_message'),
      })
    }
  }

  const handleFormChange = (updatedElements: CoCreationFormElement[]) => {
    if (!agentState || !setAgentState) return
    const updatedQuestionnaire: QuestionnaireType = {
      ...agentState.questionnaire,
      elements: FromFormToQuestionnaire(updatedElements),
    }
    setAgentState({
      ...agentState,
      questionnaire: updatedQuestionnaire,
    })
  }

  if (loadingForm) {
    return <Loading expand />
  }

  if (!agentState) {
    return <Loading expand />
  }

  const elements = FromQuestionnaireToForm(agentState.questionnaire.elements)

  return (
    <Box flexGrow height="full" padding="s16" paddingBottom="s96">
      <Box gap="s16" width="s600" alignSelf="center" height="full">
        <CoCreationForm
          isEditMode={formIsDraft}
          elements={elements}
          onChange={handleFormChange}
          allowedQuestionTypes={allowedQuestionTypes}
          applyingChanges={isThinking}
        />
      </Box>
      {formIsDraft && <FormEditorActions isDirty={isDirty} onSave={onSave} onDiscard={onDiscard} />}
    </Box>
  )
}

export default NewFormEditor
